import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Search, FileText, DollarSign, AlertTriangle, CheckCircle, Clock, Trash2, Paperclip, Upload, X, Download } from 'lucide-react';
import { toast } from 'sonner';
import { format, isAfter, differenceInDays } from 'date-fns';
import { formatInCST } from '../utils/timezone';

export default function AccountsPayable() {
  const [bills, setBills] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [billForm, setBillForm] = useState({
    vendor_id: '',
    bill_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'draft',
    line_items: [{ description: '', quantity: 1, rate: 0, amount: 0 }],
    notes: ''
  });
  const [vendorForm, setVendorForm] = useState({
    vendor_name: '',
    email: '',
    phone: '',
    contact_name: '',
    address: '',
    payment_terms: 'Net 30'
  });

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async () => {
    try {
      const [billsRes, vendorsRes, summaryRes] = await Promise.all([
        api.get('/bills', { params: statusFilter !== 'all' ? { status: statusFilter } : {} }),
        api.get('/vendors'),
        api.get('/accounting/ap-summary')
      ]);
      setBills(billsRes.data);
      setVendors(vendorsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to load AP data');
    } finally {
      setLoading(false);
    }
  };

  const handleVendorSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = { ...vendorForm };
      Object.keys(dataToSubmit).forEach(key => {
        if (dataToSubmit[key] === '') delete dataToSubmit[key];
      });
      
      await api.post('/vendors', dataToSubmit);
      toast.success('Vendor created successfully');
      setVendorDialogOpen(false);
      setVendorForm({ vendor_name: '', email: '', phone: '', contact_name: '', address: '', payment_terms: 'Net 30' });
      loadData();
    } catch (error) {
      toast.error('Failed to create vendor');
    }
  };

  const calculateLineAmount = (quantity, rate) => quantity * rate;

  const updateLineItem = (index, field, value) => {
    const newItems = [...billForm.line_items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'rate') {
      newItems[index].amount = calculateLineAmount(
        parseFloat(newItems[index].quantity) || 0,
        parseFloat(newItems[index].rate) || 0
      );
    }
    
    setBillForm({ ...billForm, line_items: newItems });
  };

  const addLineItem = () => {
    setBillForm({
      ...billForm,
      line_items: [...billForm.line_items, { description: '', quantity: 1, rate: 0, amount: 0 }]
    });
  };

  const removeLineItem = (index) => {
    if (billForm.line_items.length > 1) {
      setBillForm({
        ...billForm,
        line_items: billForm.line_items.filter((_, i) => i !== index)
      });
    }
  };

  const calculateTotals = () => {
    const subtotal = billForm.line_items.reduce((sum, item) => sum + (item.amount || 0), 0);
    const tax = subtotal * 0.0; // No tax for now
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const handleBillSubmit = async (e) => {
    e.preventDefault();
    try {
      const vendor = vendors.find(v => v.id === billForm.vendor_id);
      const { subtotal, tax, total } = calculateTotals();
      
      const dataToSubmit = {
        ...billForm,
        vendor_name: vendor?.vendor_name || '',
        subtotal,
        tax_amount: tax,
        total,
        balance_due: total,
        amount_paid: 0
      };
      
      if (!dataToSubmit.notes) delete dataToSubmit.notes;
      
      const response = await api.post('/bills', dataToSubmit);
      const newBillId = response.data.id;

      // Upload pending files
      if (pendingFiles.length > 0 && newBillId) {
        setUploading(true);
        for (const file of pendingFiles) {
          try {
            const formData = new FormData();
            formData.append('file', file);
            await api.post(`/bills/${newBillId}/attachments`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch (err) {
            toast.error(`Failed to upload ${file.name}`);
          }
        }
        setUploading(false);
      }

      toast.success('Bill created successfully');
      setDialogOpen(false);
      setPendingFiles([]);
      setBillForm({
        vendor_id: '',
        bill_date: new Date().toISOString().split('T')[0],
        due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        status: 'draft',
        line_items: [{ description: '', quantity: 1, rate: 0, amount: 0 }],
        notes: ''
      });
      loadData();
    } catch (error) {
      toast.error('Failed to create bill');
    }
  };

  const handleStatusChange = async (billId, newStatus) => {
    try {
      const bill = bills.find(b => b.id === billId);
      const updateData = { status: newStatus };
      
      if (newStatus === 'paid') {
        updateData.amount_paid = bill.total;
        updateData.balance_due = 0;
      }
      
      await api.put(`/bills/${billId}`, updateData);
      toast.success('Bill status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update bill');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      pending_approval: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors.draft;
  };

  const isOverdue = (bill) => {
    return bill.status !== 'paid' && bill.status !== 'cancelled' && 
           bill.due_date && isAfter(new Date(), new Date(bill.due_date));
  };

  const filteredBills = bills.filter(bill => {
    const matchesSearch = !searchTerm || 
      bill.bill_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bill.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || bill.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const { subtotal: calcSubtotal, tax: calcTax, total: calcTotal } = calculateTotals();

  if (loading) {
    return (
      <div className="co-page co-scope co-settings flex flex-col items-center justify-center min-h-[60vh]">
        <img src="/cipherone-logo.png" alt="Loading" className="h-16 w-16 logo-spin mb-4" />
        <div className="text-lg text-gray-600">Loading AP data...</div>
      </div>
    );
  }

  return (
    <div className="co-page co-scope co-settings space-y-4 sm:space-y-6" data-testid="ap-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Accounts Payable</h1>
          <p className="text-gray-600 text-sm sm:text-base mt-1">Manage vendor bills and payments</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={vendorDialogOpen} onOpenChange={setVendorDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Add Vendor
              </Button>
            </DialogTrigger>
            <DialogContent className="co-scope co-settings max-w-xl">
              <DialogHeader>
                <DialogTitle>Add New Vendor</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleVendorSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="vendor_name">Vendor Name *</Label>
                    <Input id="vendor_name" value={vendorForm.vendor_name} onChange={(e) => setVendorForm({...vendorForm, vendor_name: e.target.value})} required />
                  </div>
                  <div>
                    <Label htmlFor="contact_name">Contact Name</Label>
                    <Input id="contact_name" value={vendorForm.contact_name} onChange={(e) => setVendorForm({...vendorForm, contact_name: e.target.value})} />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" value={vendorForm.email} onChange={(e) => setVendorForm({...vendorForm, email: e.target.value})} />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input id="phone" value={vendorForm.phone} onChange={(e) => setVendorForm({...vendorForm, phone: e.target.value})} />
                  </div>
                  <div>
                    <Label htmlFor="payment_terms">Payment Terms</Label>
                    <Select value={vendorForm.payment_terms} onValueChange={(value) => setVendorForm({...vendorForm, payment_terms: value})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Net 15">Net 15</SelectItem>
                        <SelectItem value="Net 30">Net 30</SelectItem>
                        <SelectItem value="Net 60">Net 60</SelectItem>
                        <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="address">Address</Label>
                    <Textarea id="address" value={vendorForm.address} onChange={(e) => setVendorForm({...vendorForm, address: e.target.value})} rows={2} />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setVendorDialogOpen(false)}>Cancel</Button>
                  <Button type="submit">Create Vendor</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-bill-button">
                <Plus className="h-4 w-4 mr-2" />
                New Bill
              </Button>
            </DialogTrigger>
            <DialogContent className="co-scope co-settings max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Bill</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleBillSubmit} className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Vendor *</Label>
                    <Select value={billForm.vendor_id} onValueChange={(value) => setBillForm({...billForm, vendor_id: value})} required>
                      <SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger>
                      <SelectContent>
                        {vendors.map((vendor) => (
                          <SelectItem key={vendor.id} value={vendor.id}>{vendor.vendor_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Bill Date *</Label>
                    <Input type="date" value={billForm.bill_date} onChange={(e) => setBillForm({...billForm, bill_date: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Due Date</Label>
                    <Input type="date" value={billForm.due_date} onChange={(e) => setBillForm({...billForm, due_date: e.target.value})} />
                  </div>
                </div>
                
                <div>
                  <Label>Line Items</Label>
                  <div className="space-y-2">
                    {billForm.line_items.map((item, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 items-center">
                        <Input className="col-span-5" placeholder="Description" value={item.description} onChange={(e) => updateLineItem(index, 'description', e.target.value)} />
                        <Input className="col-span-2" type="number" placeholder="Qty" value={item.quantity} onChange={(e) => updateLineItem(index, 'quantity', e.target.value)} />
                        <Input className="col-span-2" type="number" placeholder="Rate" value={item.rate} onChange={(e) => updateLineItem(index, 'rate', e.target.value)} />
                        <Input className="col-span-2" type="number" value={item.amount.toFixed(2)} readOnly />
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeLineItem(index)} disabled={billForm.line_items.length === 1}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addLineItem} className="mt-2">
                    <Plus className="h-3 w-3 mr-1" /> Add Line
                  </Button>
                </div>
                
                <div className="bg-gray-50 p-4 rounded space-y-2">
                  <div className="flex justify-between"><span>Subtotal:</span><span className="font-semibold">${calcSubtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span>Tax:</span><span className="font-semibold">${calcTax.toFixed(2)}</span></div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2"><span>Total:</span><span>${calcTotal.toFixed(2)}</span></div>
                </div>
                
                <div>
                  <Label>Notes</Label>
                  <Textarea value={billForm.notes} onChange={(e) => setBillForm({...billForm, notes: e.target.value})} rows={2} />
                </div>

                {/* Attachments */}
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Paperclip className="h-4 w-4" />
                    Attachments
                  </Label>
                  <div className="border-2 border-dashed border-neutral-200 rounded-lg p-4 hover:border-neutral-300 transition-colors">
                    <input
                      type="file"
                      multiple
                      id="bill-attachments"
                      className="hidden"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        setPendingFiles(prev => [...prev, ...files]);
                        e.target.value = '';
                      }}
                      data-testid="bill-attachment-input"
                    />
                    <label htmlFor="bill-attachments" className="flex flex-col items-center gap-2 cursor-pointer">
                      <Upload className="h-6 w-6 text-neutral-400" />
                      <span className="text-sm text-neutral-500">Click to upload files (invoices, receipts, etc.)</span>
                    </label>
                  </div>
                  {pendingFiles.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {pendingFiles.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-neutral-50 rounded text-sm">
                          <div className="flex items-center gap-2">
                            <Paperclip className="h-3.5 w-3.5 text-neutral-400" />
                            <span className="truncate max-w-[300px]">{file.name}</span>
                            <span className="text-xs text-neutral-400">({(file.size / 1024).toFixed(1)} KB)</span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setPendingFiles(prev => prev.filter((_, i) => i !== idx))}
                            className="h-6 w-6 p-0"
                          >
                            <X className="h-3.5 w-3.5 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit">Create Bill</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Payable</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">${summary.total_payable.toFixed(2)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                Overdue Bills
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{summary.overdue_bills}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Clock className="h-4 w-4 text-yellow-600" />
                Due Soon (7 days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{summary.upcoming_bills}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                Paid This Month
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">${summary.paid_this_month.toFixed(2)}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 flex-1">
              <Search className="h-5 w-5 text-gray-400" />
              <Input placeholder="Search bills..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="max-w-sm" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending_approval">Pending Approval</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {/* Mobile Card View */}
          <div className="md:hidden space-y-2" data-testid="ap-mobile-cards">
            {filteredBills.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                No bills found
              </div>
            ) : filteredBills.map((bill) => (
              <div
                key={bill.id}
                className={`border rounded-lg p-3 ${isOverdue(bill) ? 'bg-red-50 border-red-200' : 'bg-[#081a25]'}`}
                data-testid={`ap-card-${bill.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-semibold">{bill.bill_number}</span>
                      <Badge className={getStatusColor(bill.status)}>{bill.status.replace('_', ' ')}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">{bill.vendor_name}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-semibold">${bill.total.toFixed(2)}</div>
                    {bill.balance_due > 0 && bill.balance_due !== bill.total && (
                      <div className="text-xs text-red-600">Due: ${bill.balance_due.toFixed(2)}</div>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                  <span>Due {bill.due_date ? formatInCST(bill.due_date, 'MMM d, yyyy') : '-'}</span>
                  {bill.status !== 'paid' && (
                    <Select value={bill.status} onValueChange={(value) => handleStatusChange(bill.id, value)}>
                      <SelectTrigger className="w-[100px] h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="pending_approval">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="cancelled">Cancel</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bill Number</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Bill Date</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Balance Due</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBills.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                    <FileText className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                    No bills found
                  </TableCell>
                </TableRow>
              ) : (
                filteredBills.map((bill) => (
                  <TableRow key={bill.id} className={isOverdue(bill) ? 'bg-red-50' : ''}>
                    <TableCell className="font-mono text-sm">{bill.bill_number}</TableCell>
                    <TableCell>{bill.vendor_name}</TableCell>
                    <TableCell>{formatInCST(bill.bill_date, 'MMM d, yyyy')}</TableCell>
                    <TableCell>
                      {bill.due_date ? formatInCST(bill.due_date, 'MMM d, yyyy') : '-'}
                      {isOverdue(bill) && (
                        <Badge className="ml-2 bg-red-100 text-red-800">Overdue</Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-semibold">${bill.total.toFixed(2)}</TableCell>
                    <TableCell className="font-semibold">${bill.balance_due.toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(bill.status)}>
                        {bill.status.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {bill.status !== 'paid' && (
                        <Select value={bill.status} onValueChange={(value) => handleStatusChange(bill.id, value)}>
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="pending_approval">Pending Approval</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="paid">Mark as Paid</SelectItem>
                            <SelectItem value="cancelled">Cancel</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
